import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Card, Table, Button, Form, Badge, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { TranslatorContext } from "../../../../context/Translator";
import PageLayout from "../../../../layouts/PageLayout";

export default function StoreSellerList() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [sellers, setSellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [accountStatusFilter, setAccountStatusFilter] = useState("");

    const fetchSellers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/store-sellers`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        ...(search ? { search } : {}),
                        ...(accountStatusFilter ? { account_status: accountStatusFilter } : {}),
                    },
                }
            );

            // Handling both formats: response.data.store_sellers or direct data array
            if (response.data?.success) {
                const dataList = response.data.store_sellers || response.data.data || [];
                setSellers(dataList);
            } else if (Array.isArray(response.data)) {
                setSellers(response.data);
            }
        } catch (err) {
            console.error("Error fetching store sellers:", err);
            setError(err.response?.data?.message || "Failed to fetch store sellers.");
        } finally {
            setLoading(false);
        }
    }, [search, accountStatusFilter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchSellers();
        }, 400);
        return () => clearTimeout(timer);
    }, [fetchSellers]);

    const getStatusBadge = (status) => {
        // Handling both boolean or string statuses safely
        if (status === true || status === "approved" || status === "1") {
            return <Badge bg="success">{t("Approved")}</Badge>;
        }
        switch (String(status).toLowerCase()) {
            case "pending":
                return <Badge bg="warning" text="dark">{t("Pending")}</Badge>;
            case "rejected":
                return <Badge bg="danger">{t("Rejected")}</Badge>;
            case "suspended":
            case "false":
            case "0":
                return <Badge bg="dark">{t("Suspended/Inactive")}</Badge>;
            default:
                return <Badge bg="secondary">{status || "N/A"}</Badge>;
        }
    };

    const handleRowClick = (sellerId) => {
        if (sellerId) {
            navigate(`/admin/store-sellers/${sellerId}`);
        }
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">{t("Store Sellers")}</h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">{t("Home")}</Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">{t("Store Sellers")}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Body className="p-4">
                            {error && (
                                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                    {error}
                                </Alert>
                            )}

                            {/* Filters */}
                            <Row className="g-3 mb-4">
                                <Col md={6} lg={4}>
                                    <Form.Control
                                        type="text"
                                        placeholder={t("Search by Name, Email, Phone...")}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </Col>
                                <Col md={4} lg={3}>
                                    <Form.Select
                                        value={accountStatusFilter}
                                        onChange={(e) => setAccountStatusFilter(e.target.value)}
                                    >
                                        <option value="">{t("All Statuses")}</option>
                                        <option value="pending">{t("Pending")}</option>
                                        <option value="approved">{t("Approved")}</option>
                                        <option value="rejected">{t("Rejected")}</option>
                                        <option value="suspended">{t("Suspended")}</option>
                                    </Form.Select>
                                </Col>
                            </Row>

                            {/* Table */}
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted">{t("Loading store sellers...")}</p>
                                </div>
                            ) : sellers.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="bi bi-inbox display-4"></i>
                                    <p className="mt-2">{t("No store sellers found.")}</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover align="middle" className="mc-table">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>{t("Seller Name")}</th>
                                                <th>{t("Store Name")}</th>
                                                <th>{t("Contact Info")}</th>
                                                <th>{t("Status")}</th>
                                                <th className="text-end">{t("Actions")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sellers.map((user, idx) => {
                                                const profile = user.store_seller || {};
                                                return (
                                                    <tr
                                                        key={user.id}
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => handleRowClick(user.id)}
                                                    >
                                                        <td>{idx + 1}</td>
                                                        <td>
                                                            <div className="fw-semibold text-primary">{user.name || "N/A"}</div>
                                                            <small className="text-muted">@{user.nickname || "N/A"}</small>
                                                        </td>
                                                        <td>
                                                            <span className="fw-medium">
                                                                {profile.store?.name || profile.store_name || user.store_name || "N/A"}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div><small>{user.email || "N/A"}</small></div>
                                                            <div><small className="text-muted">{user.phone || profile.whatsapp || "N/A"}</small></div>
                                                        </td>
                                                        <td>{getStatusBadge(profile.account_status ?? user.status)}</td>
                                                        <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                                            <div className="d-flex justify-content-end gap-2">
                                                                <Button
                                                                    variant="info"
                                                                    size="sm"
                                                                    onClick={() => navigate(`/admin/store-sellers/${user.id}`)}
                                                                >
                                                                    <i className="bi bi-eye me-1"></i> {t("View")}
                                                                </Button>
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                        navigate(`/admin/store-sellers/${user.id}/edit`, {
                                                                            state: { sellerData: user },
                                                                        })
                                                                    }
                                                                >
                                                                    <i className="bi bi-pencil me-1"></i> {t("Edit")}
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}